import { Component, effect, inject, input, viewChild } from '@angular/core';
import { CdkPortal } from '@angular/cdk/portal';
import { PageHeaderService } from '../../../core/services/page-header.service';

/**
 * A page's own top bar: its heading, what sits next to it and its actions, in place of the route
 * title. Nothing renders where the component is written; the content is carried into the layout's
 * top bar and taken back out when the page goes away.
 *
 * Slots: `[pageHeaderLeading]` before the heading (a back button), the default slot after it
 * (status, labels) and `[pageHeaderActions]` on the right.
 *
 * The header is a size container named `page-header`: how much top bar is left depends on whether
 * the rail is showing, not only on the viewport, so a page drops what does not fit with
 * `@container page-header (max-width: …)`.
 */
@Component({
    selector: 'app-page-header',
    imports: [CdkPortal],
    template: `
        <ng-template cdkPortal>
            <div class="page-header">
                <div class="page-header__bar">
                    <div class="page-header__start">
                        <ng-content select="[pageHeaderLeading]" />
                        <h1 class="page-header__title">{{ heading() }}</h1>
                        <ng-content />
                    </div>

                    <div class="page-header__actions">
                        <ng-content select="[pageHeaderActions]" />
                    </div>
                </div>
            </div>
        </ng-template>
    `,
    styles: `
        :host {
            display: none;
        }

        .page-header {
            flex: 1 1 0;
            min-width: 0;
            container: page-header / inline-size;
        }

        .page-header__bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1.5rem;
        }

        .page-header__start {
            display: flex;
            align-items: center;
            gap: 0.875rem;
            min-width: 0;
        }

        .page-header__title {
            margin: 0;
            min-width: 0;
            overflow: hidden;
            font-size: 1.125rem;
            font-variant-numeric: tabular-nums;
            letter-spacing: -0.01em;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .page-header__actions {
            display: flex;
            flex: none;
            align-items: center;
            gap: 0.625rem;
        }

        /*
         * The top bar's controls run a notch under a form button: a 34px square before the heading,
         * 38px actions with 13px labels.
         */
        .page-header__start {
            --p-button-padding-y: 0;
            --p-button-icon-only-width: 2.125rem;
            --p-button-border-radius: 0.5rem;
        }

        .page-header__actions {
            --p-button-font-size: 0.8125rem;
            --p-button-padding-y: 0;
            --p-button-padding-x: 0.875rem;
            --p-button-icon-only-width: 2.375rem;
            --p-button-border-radius: 0.5rem;
        }

        .page-header__start ::ng-deep .p-button {
            height: 2.125rem;
        }

        .page-header__actions ::ng-deep .p-button {
            height: 2.375rem;
        }

        .page-header__actions ::ng-deep .p-button-icon {
            font-size: 0.9375rem;
        }

        /* Aura greys the label of an outlined secondary button below AA; in the top bar it reads as text. */
        .page-header ::ng-deep .p-button-outlined.p-button-secondary {
            color: var(--p-text-color);
        }

        .page-header ::ng-deep .p-button-outlined.p-button-secondary .p-button-icon {
            color: var(--p-text-muted-color);
        }

        /* On a phone every pixel goes to the heading, so the actions drop back to a form button's size. */
        @container page-header (max-width: 24rem) {
            .page-header__bar {
                gap: 0.5rem;
            }

            .page-header__start {
                gap: 0.5rem;
            }

            .page-header__title {
                font-size: 1rem;
            }

            .page-header__actions {
                --p-button-icon-only-width: 2.25rem;
            }

            .page-header__actions ::ng-deep .p-button {
                height: 2.25rem;
            }
        }
    `
})
export class PageHeaderComponent {
    private readonly pageHeader = inject(PageHeaderService);
    private readonly portal = viewChild(CdkPortal);

    readonly heading = input.required<string>();

    constructor() {
        effect((onCleanup) => {
            const portal = this.portal();
            if (!portal) {
                return;
            }

            this.pageHeader.show(portal);
            onCleanup(() => this.pageHeader.clear(portal));
        });
    }
}
